import React, { PropsWithChildren, useCallback } from 'react';
import { buildURI, toCSV } from '../core';

export type DataType = string | string[][] | object[];

export type HeaderInfo = {
  label: string;
  key: string;
};

export interface CSVLinkProps {
  data: DataType | (() => DataType);
  headers?: string[] | HeaderInfo[];
  target?: string;
  separator?: string;
  filename?: string;
  uFEFF?: boolean;
  onClick?: (event: React.MouseEvent, done?: (proceed: boolean) => void) => any;
  asyncOnClick?: boolean;
  linkRef?: React.Ref<HTMLAnchorElement>;
  className?: string;
  role?: string;
}

const CSVLink: React.FC<PropsWithChildren<CSVLinkProps>> = ({
  data,
  headers,
  target = '_self',
  separator = ',',
  filename = 'generatedBy_react-csv.csv',
  uFEFF = true,
  onClick,
  asyncOnClick = false,
  children,
  linkRef,
  className,
  role,
}) => {
  /**
   * In IE11 this method will trigger the file download
   */
  const handleLegacy = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, isAsync = false) => {
      // If this browser is IE 11, it does not support the `download` attribute
      // @ts-expect-error
      if (window.navigator.msSaveOrOpenBlob) {
        // Stop the click propagation
        event.preventDefault();

        const csvData = isAsync && typeof data === 'function' ? data() : data;

        const blob = new Blob([
          uFEFF ? '\uFEFF' : '',
          toCSV(csvData, headers, separator),
        ]);
        // @ts-expect-error
        window.navigator.msSaveBlob(blob, filename);

        return false;
      }
    },
    [data, headers, filename, separator, uFEFF]
  );

  const handleAsyncClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      const done = (proceed) => {
        if (proceed === false) {
          event.preventDefault();
          return;
        }
        handleLegacy(event, true);
      };

      onClick!(event, done);
    },
    [onClick, handleLegacy]
  );

  const handleSyncClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      const stopEvent = onClick!(event) === false;
      if (stopEvent) {
        event.preventDefault();
        return;
      }
      handleLegacy(event);
    },
    [onClick, handleLegacy]
  );

  const handleClick = () => {
    return (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (typeof onClick === 'function') {
        return asyncOnClick ? handleAsyncClick(event) : handleSyncClick(event);
      }
      handleLegacy(event);
    };
  };

  const isNodeEnvironment = typeof window === 'undefined';
  const href = isNodeEnvironment
    ? ''
    : buildURI(data, uFEFF, headers, separator);

  return (
    <a
      className={className}
      role={role}
      ref={linkRef}
      download={filename}
      target={target}
      href={href}
      onClick={handleClick()}
    >
      {children}
    </a>
  );
};

export default CSVLink;
